import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Emoji, EmojiStyle } from "emoji-picker-react";
import "sweetalert2/dist/sweetalert2.min.css";
import "./game.css";
import { graphemeToUnified, splitGraphemes } from "../../utils/grapheme";
import { selectAdminToken } from "../../admin/sessionSlice";
import { getDvSourceImageUrl, toCssUrlValue } from "../../functions/themeAssets";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";

const MAX_ATTEMPTS = 3;
/** Theme `total_question` cap — must match admin `themeupdate.jsx` */
const MAX_THEMED_QUESTIONS = 20;
const USER_DATA_KEY = "userData";

function resolveReportsRole(ud) {
  const r = String(ud?.role || "").trim();
  const s = String(ud?.source || "").trim().toUpperCase();
  if (r.toLowerCase() === "demobypass") return "demobypass";
  if (r === "GUEST_USER" || s === "GUEST_USER") return "GUEST_USER";
  return "ORG_USER";
}

function stageUserTypeLabel(ud) {
  const rr = resolveReportsRole(ud);
  if (rr === "demobypass") return "DEMO_USER";
  if (rr === "GUEST_USER") return "GUEST_USER";
  return "ORG_USER";
}

function readSessionUserData() {
  try {
    return JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function isDemoSessionUser(ud) {
  return (
    resolveReportsRole(ud) === "demobypass" ||
    String(ud?.source ?? "").trim().toUpperCase() === "DEMO"
  );
}

function readSessionReportId() {
  try {
    const u = JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}");
    const id = u?.reportId ?? u?.report_id;
    return id != null && String(id).trim() !== "" ? String(id).trim() : null;
  } catch {
    return null;
  }
}

function patchSessionReportId(reportId) {
  try {
    const raw = sessionStorage.getItem(USER_DATA_KEY);
    const u = raw ? JSON.parse(raw) : {};
    if (!reportId) return;
    sessionStorage.setItem(USER_DATA_KEY, JSON.stringify({ ...u, reportId: String(reportId) }));
  } catch (_) {}
}

function SvgIconStar({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
    </svg>
  );
}

const MAX_ANSWER_LEN = 50;

function normalizeAnswer(v) {
  return String(v ?? "").trim().toLowerCase();
}

function parseThemeBoolTrueDefault(val, fallbackTrue = true) {
  if (val === undefined || val === null || val === "") return fallbackTrue;
  if (typeof val === "boolean") return val;
  const s = String(val).trim().toLowerCase();
  if (s === "false" || s === "0" || s === "no") return false;
  if (s === "true" || s === "1" || s === "yes") return true;
  return fallbackTrue;
}

function clampIntGameplay(n, min, max, fallback) {
  const x = parseInt(String(n), 10);
  if (Number.isNaN(x)) return fallback;
  return Math.min(max, Math.max(min, x));
}

function normalizeBoxColor(themeData) {
  const raw = String(themeData?.box_color ?? themeData?.boxcolor ?? "#334155").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(raw)) return `#${raw.slice(1, 7)}`.toLowerCase();
  return "#334155";
}

/** DB uses `background_desk`/`background_mob` (upload UI) or legacy `*_view_image` keys. */
function resolveThemeBackdropKey(themeData, preferMobileViewport) {
  if (!themeData) return "";
  // Prefer upload keys so we don't surface 404 placeholder paths from stale legacy fields.
  const desk = themeData.background_desk ?? themeData.desktop_view_image;
  const mob = themeData.background_mob ?? themeData.mobile_view_image;
  const ds = desk != null ? String(desk).trim() : "";
  const ms = mob != null ? String(mob).trim() : "";
  if (preferMobileViewport && ms) return ms;
  if (!preferMobileViewport && ds) return ds;
  return ds || ms;
}

function formatMmSs(totalSeconds) {
  const s = Math.max(0, Number(totalSeconds) || 0);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

/** Inverse of formatMmSs — matches `stages.time` / API payloads */
function parseMmSsToSeconds(t) {
  const m = /^(\d+):(\d{1,2})$/.exec(String(t ?? "0:00").trim());
  if (!m) return 0;
  return Number(m[1]) * 60 + Number(m[2]);
}

function isSweetAlertVisible() {
  if (typeof document === "undefined") return false;
  return Boolean(document.querySelector("div.swal2-container.swal2-shown"));
}

/** `stages.questions` JSON array vs current question order — tolerate formatting differences */
function questionOrderSnapMatches(dbSnap, mapped) {
  const expected = JSON.stringify(mapped.map((q) => q.sortOrder));
  const raw = dbSnap != null ? String(dbSnap).trim() : "";
  if (!raw || raw === "null") return true;
  try {
    const a = JSON.parse(raw);
    const b = JSON.parse(expected);
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return raw === expected;
  }
}

function parseRequiredTotalQuestions(themeData) {
  const n = parseInt(String(themeData?.total_question ?? ""), 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.min(MAX_THEMED_QUESTIONS, n);
}

/** Match `.question-emoji-between` / `.question-emoji-row--compact` horizontal footprint (px). */
function estimateEmojiSeparatorWidthPx(viewportWidth, compact) {
  const w = Number.isFinite(viewportWidth) && viewportWidth > 240 ? viewportWidth : 400;
  if (compact) {
    const fs = Math.min(22, Math.max(14, w * 0.038));
    const ph = Math.min(6, Math.max(2, w * 0.01));
    return Math.ceil(2 * ph + fs * 0.5 + 2);
  }
  const fs = Math.min(36, Math.max(22, w * 0.055));
  const ph = Math.min(12, Math.max(6, w * 0.02));
  return Math.ceil(2 * ph + fs * 0.5 + 4);
}

/**
 * Fit emoji cells + “+” in one row across the full game content width (no horizontal scroll).
 * Slot ≈ window minus `.game-main-panel` margins and `.game-content` horizontal padding.
 */
function computeQuestionEmojiLayout(count, viewportWidth) {
  if (count < 1) return { cellPx: 88, emojiPx: 76 };
  const w = Number.isFinite(viewportWidth) && viewportWidth > 240 ? viewportWidth : 400;
  const panelMarginX = 2 * Math.max(10, Math.min(20, w * 0.028));
  const contentPadX = 2 * Math.max(12, Math.min(32, w * 0.04));
  const slot = Math.max(260, Math.floor(w - panelMarginX - contentPadX - 8));
  const compact = count >= 6;
  const sep = estimateEmojiSeparatorWidthPx(w, compact);
  const betweenTotal = (count - 1) * sep;
  const slack = 6;
  let cellPx = Math.floor((slot - betweenTotal - slack) / count);
  cellPx = Math.max(22, Math.min(100, cellPx));
  while (count * cellPx + betweenTotal + slack > slot && cellPx > 22) {
    cellPx -= 1;
  }
  const emojiPx = Math.max(16, Math.min(cellPx - 6, 92));
  return { cellPx, emojiPx };
}

const looksLikeQuestionImageKey = (s) => /\.(png|jpe?g|gif|webp)$/i.test(String(s || "").trim());

/** Same mapping as gameplay load — rows with an image key and/or emoji question text */
function mapQuestionsFromApiPayload(data) {
  const list = Array.isArray(data?.questions) ? data.questions : [];
  return list
    .sort((a, b) => (Number(a.sort_order ?? 0) || 0) - (Number(b.sort_order ?? 0) || 0))
    .map((q, qi) => {
      const sq = Number(q.sort_order ?? 0);
      const img = String(q.image ?? "").trim();
      const qu = String(q.question ?? "").trim();

      let imageName = "";
      let emojiQuestion = "";

      if (img && looksLikeQuestionImageKey(img)) {
        imageName = img;
      } else if (!img && qu && looksLikeQuestionImageKey(qu)) {
        imageName = qu;
      } else if (qu) {
        emojiQuestion = qu;
      }

      return {
        id: q.id ?? null,
        sortOrder: Number.isFinite(sq) && sq >= 1 ? sq : qi + 1,
        imageName,
        emojiQuestion,
        answer: String(q.answer ?? "").trim(),
        hint: String(q.hint ?? "").trim(),
      };
    })
    .filter((q) => q.imageName || q.emojiQuestion);
}

/* ─── Confetti hook — fullscreen burst ───────────────────────────────── */
function resizeConfettiCanvas(canvas) {
  if (!canvas || typeof window === "undefined") return { W: 0, H: 0 };
  const W = Math.max(window.innerWidth, 320);
  const H = Math.max(window.innerHeight, 420);
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.floor(W * dpr);
  canvas.height = Math.floor(H * dpr);
  canvas.style.width = `${W}px`;
  canvas.style.height = `${H}px`;
  const ctx = canvas.getContext("2d");
  if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { W, H };
}

function useConfetti(canvasRef) {
  const afRef = useRef(null);

  const fire = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof window === "undefined") return;
    const { W, H } = resizeConfettiCanvas(canvas);
    if (!W || !H) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);
    const colors = ["#f6c445", "#7c3aed", "#4ade80", "#f87171", "#60a5fa", "#fb923c", "#e879f9", "#ffffff"];
    const particles = [];
    const spreadY = Math.max(H * 0.42, 220);

    for (let i = 0; i < 260; i++) {
      const fromRight = i % 2 === 0;
      particles.push({
        x: fromRight ? W + Math.random() * 40 : -Math.random() * 40,
        y: Math.random() * spreadY + H * 0.02,
        vx: fromRight ? -(4 + Math.random() * 14) : 4 + Math.random() * 14,
        vy: -(1.8 + Math.random() * 6.5),
        g: 0.12 + Math.random() * 0.14,
        rot: Math.random() * 360,
        rotV: (Math.random() - 0.5) * 12,
        w: 6 + Math.random() * 12,
        h: 4 + Math.random() * 7,
        color: colors[i % colors.length],
        alpha: 1,
        wobble: Math.random() * Math.PI * 2,
        wobbleSpeed: 0.06 + Math.random() * 0.12,
      });
    }

    if (afRef.current) cancelAnimationFrame(afRef.current);
    let tick = 0;

    function draw() {
      ctx.clearRect(0, 0, W, H);
      let alive = false;
      for (const p of particles) {
        p.vy += p.g;
        p.x += p.vx + Math.sin(p.wobble) * 2.2;
        p.y += p.vy;
        p.rot += p.rotV;
        p.wobble += p.wobbleSpeed;
        if (tick > 40) p.alpha = Math.max(0, p.alpha - 0.016);
        if (p.alpha > 0.01 && p.y < H + 80) alive = true;
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rot * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      tick++;
      if (alive) afRef.current = requestAnimationFrame(draw);
      else ctx.clearRect(0, 0, W, H);
    }
    draw();
  }, [canvasRef]);

  return fire;
}

export default function GamePage() {
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData, status: themeStatus } = useSelector((state) => state.theme);

  const storedUserData = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const [rulesOpen, setRulesOpen] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  /** Sync for focus helper (timeouts / animation frames) without stale closures */
  const rulesOpenRef = useRef(false);
  rulesOpenRef.current = rulesOpen;
  const hintOpenRef = useRef(false);
  hintOpenRef.current = hintOpen;
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  const [idx, setIdx] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [wrongTotal, setWrongTotal] = useState(0);
  const [points, setPoints] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [answer, setAnswer] = useState("");
  const [submitLocked, setSubmitLocked] = useState(false);
  const [isBuzzing, setIsBuzzing] = useState(false);
  /** Full-screen blocked symbol flash (wrong submit); timer-out uses buzz + reveal */
  const [blockedStrikeFlash, setBlockedStrikeFlash] = useState(false);
  /** ≥0 = countdown ticks; −1 = not using per-question timer this moment */
  const [questionRemaining, setQuestionRemaining] = useState(-1);
  const [pointsFlash, setPointsFlash] = useState(null);
  const [narrowViewport, setNarrowViewport] = useState(() => (
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  ));
  const [viewportW, setViewportW] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 720));

  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const stageIdRef = useRef(null);
  const elapsedRef = useRef(0);
  const rulesDialogRef = useRef(null);
  const hintDialogRef = useRef(null);
  const answerInputRef = useRef(null);
  const reportIdRef = useRef(readSessionReportId());
  const answerHistoryRef = useRef([]);

  const fireConfetti = useConfetti(canvasRef);

  const questionEmojiLayout = useMemo(() => {
    const cur = questions[idx] || null;
    const text = String(cur?.emojiQuestion ?? "").trim();
    const glyphs = text ? splitGraphemes(text) : [];
    const n = glyphs.length;
    const dims = computeQuestionEmojiLayout(n, viewportW);
    return { glyphs, ...dims };
  }, [questions, idx, viewportW]);

  const textColor = String(themeData?.textcolor ?? "#ffffff");
  const boxColorSolid = normalizeBoxColor(themeData);
  const useTimeoutQs = parseThemeBoolTrueDefault(themeData?.use_timeout, true);
  const questionBudgetSec = useMemo(() => {
    if (!useTimeoutQs) return 0;
    const m = clampIntGameplay(themeData?.question_timeout_minutes, 0, 59, 0);
    const s = clampIntGameplay(themeData?.question_timeout_seconds, 0, 59, 0);
    let t = m * 60 + s;
    if (t < 5) t = 90;
    return t;
  }, [themeData, useTimeoutQs]);
  const iconBg = String(themeData?.icon_bg_color ?? "#7c3aed");
  const buttonColor = String(themeData?.button_color ?? "#f6c445");
  const buttonTextColor = String(themeData?.button_Textcolor ?? "#111111");

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const sync = () => setNarrowViewport(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", sync);
    else mq.addListener(sync);
    sync();
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else mq.removeListener(sync);
    };
  }, []);

  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const themeBackdropRaw = resolveThemeBackdropKey(themeData, narrowViewport);
  const popupBgCss = useMemo(() => {
    const u = getDvSourceImageUrl(themeBackdropRaw);
    return u ? toCssUrlValue(u) : "none";
  }, [themeBackdropRaw]);

  const hasPopupBackdropImage = popupBgCss !== "none";

  /** Readable on dark blurred scrim: switch to light FG when theme text is very dark */
  const popupFgColor = useMemo(() => {
    const s = String(textColor ?? "#ffffff").trim();
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})/i.exec(s.replace(/\s/g, ""));
    if (!m) return textColor;
    const r = parseInt(m[1], 16);
    const g = parseInt(m[2], 16);
    const b = parseInt(m[3], 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum < 0.42 ? "#f8fafc" : textColor;
  }, [textColor]);

  /** Shared SweetAlert styling: blurred page, theme image popup, readable scrim */
  const getGameSwalBase = useCallback(
    () => ({
      /** Do not set `background` — sweetalert assigns it as inline shorthand and wipes layered CSS. Card fill is `.game-sw2-popup--photo::before`. */
      color: popupFgColor,
      confirmButtonColor: buttonColor,
      buttonsStyling: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      customClass: {
        popup: hasPopupBackdropImage
          ? "game-sw2-popup game-sw2-popup--photo game-sw2-popup--boxed"
          : "game-sw2-popup game-sw2-popup--no-photo game-sw2-popup--boxed",
        container: "game-sw2-container",
        confirmButton: "game-sw2-confirm-btn",
        title: "game-sw2-title",
        htmlContainer: "game-sw2-html",
      },
    }),
    [popupFgColor, buttonColor, hasPopupBackdropImage]
  );

  /** Return focus to the answer box when rules/modals aren’t stealing it */
  const focusAnswerInput = useCallback(() => {
    const focus = () => {
      if (rulesOpenRef.current) return;
      if (hintOpenRef.current) return;
      if (isSweetAlertVisible()) return;
      answerInputRef.current?.focus({ preventScroll: true });
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(focus);
    });
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--game-popup-bg", popupBgCss);
    document.documentElement.style.setProperty("--game-sw2-confirm-fg", buttonTextColor);
    document.documentElement.style.setProperty("--game-panel-box", boxColorSolid);
    document.documentElement.style.setProperty("--game-sw2-box-fill", boxColorSolid);
    return () => {
      document.documentElement.style.removeProperty("--game-popup-bg");
      document.documentElement.style.removeProperty("--game-sw2-confirm-fg");
      document.documentElement.style.removeProperty("--game-panel-box");
      document.documentElement.style.removeProperty("--game-sw2-box-fill");
    };
  }, [popupBgCss, buttonTextColor, boxColorSolid]);


  useEffect(() => {
    if (!rulesOpen && !hintOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [rulesOpen, hintOpen]);

  useEffect(() => {
    if (!rulesOpen) return;
    const node = rulesDialogRef.current;
    if (!node) return;
    node.focus({ preventScroll: true });
  }, [rulesOpen]);

  useEffect(() => {
    if (!hintOpen) return;
    const node = hintDialogRef.current;
    if (!node) return;
    node.focus({ preventScroll: true });
  }, [hintOpen]);

  useEffect(() => {
    if (themeStatus === "loading" || !themeData) return;
    if (loadingQuestions || rulesOpen || hintOpen || submitLocked) return;
    if (!questions.length || idx < 0 || idx >= questions.length) return;
    focusAnswerInput();
  }, [
    attempts,
    focusAnswerInput,
    idx,
    loadingQuestions,
    questions.length,
    rulesOpen,
    hintOpen,
    submitLocked,
    themeData,
    themeStatus,
  ]);

  const rightPoints = Number(themeData?.points ?? 1) || 1;

  const backendBase = String(process.env.REACT_APP_BACKEND_URL ?? "").replace(/\/+$/, "");
  const userId = storedUserData?.userId || storedUserData?.userid || storedUserData?.id || "";

  useEffect(() => { elapsedRef.current = elapsed; }, [elapsed]);

  useEffect(() => {
    function handleResize() {
      if (canvasRef.current) resizeConfettiCanvas(canvasRef.current);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const persistProgress = useCallback(
    async ({ stageId, score, wrongcount, currentQues, answerJson }) => {
      if (!backendBase || !adminToken || !userId || stageId == null) return false;
      const timeStr = formatMmSs(elapsedRef.current);
      try {
        const res = await fetch(`${backendBase}/welcomeStageProgress`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({ userId, stageId, id: stageId, time: timeStr, total_score: score, wrongcount, currentQues, answer: answerJson ?? "[]" }),
        });
        const data = await res.json().catch(() => ({}));
        const ok = res.ok && data?.success !== false;
        if (!ok) console.warn("welcomeStageProgress failed:", data?.message || res.status);
        return ok;
      } catch (e) {
        console.warn("welcomeStageProgress error", e);
        return false;
      }
    },
    [backendBase, adminToken, userId]
  );

  const syncExternalReport = useCallback(
    async (scoreSnapshot) => {
      const ud = storedUserData;
      const rr = resolveReportsRole(ud);
      if (rr === "demobypass") return;
      let fresh;
      try { fresh = JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}"); } catch { fresh = ud; }
      const sidToken = fresh?.token ?? ud?.token ?? "";
      const reportBase = String(process.env.REACT_APP_REPORTS_BASE_URL ?? "").replace(/\/+$/, "");
      if (!reportBase || !fresh?.sessionId || !fresh?.organizationId || !fresh?.userId) return;
      const ridStored = reportIdRef.current ?? readSessionReportId();
      try {
        const payload = {
          sessionId: fresh.sessionId,
          organizationId: fresh.organizationId,
          userId: fresh.userId,
          role: rr === "GUEST_USER" ? "GUEST_USER" : "ORG_USER",
          token: sidToken || "",
          points: Number(scoreSnapshot) || 0,
          time: formatMmSs(elapsedRef.current),
          gameId: fresh.gameId ?? ud?.gameId ?? null,
          name: fresh.name ?? ud?.name ?? "",
        };
        if (ridStored) payload.reportId = ridStored;
        const resPayload = await sendReport(payload);
        let nextRid = extractReportIdFromResponse(resPayload) || extractReportIdFromResponse(resPayload?.data ?? resPayload);
        if (!nextRid && resPayload?.data && typeof resPayload.data === "object") nextRid = extractReportIdFromResponse(resPayload.data);
        const stageNumeric = stageIdRef.current != null ? Number(stageIdRef.current) : NaN;
        const stageStable = Number.isFinite(stageNumeric) ? stageNumeric : stageIdRef.current;
        if (nextRid) {
          reportIdRef.current = nextRid;
          patchSessionReportId(nextRid);
        }
        if (nextRid && !ridStored && backendBase && adminToken && userId && stageStable != null) {
          await fetch(`${backendBase}/welcomeStageLinkReport`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
            body: JSON.stringify({ userId: fresh.userId, stageId: stageStable, id: stageStable, reportId: nextRid }),
          });
        }
      } catch (e) {
        console.warn("external sendReport skipped/failed:", e?.message ?? e);
      }
    },
    [adminToken, backendBase, storedUserData, userId]
  );

  const finalizeStage = useCallback(
    async ({ stageId, score, wrongcount, currentQues }) => {
      if (!backendBase || !adminToken || !userId || stageId == null) return;
      try {
        await fetch(`${backendBase}/welcomeStageComplete`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({ userId, stageId, id: stageId, time: formatMmSs(elapsedRef.current), total_score: score, wrongcount, currentQues }),
        });
      } catch (e) { console.warn("welcomeStageComplete error", e); }
    },
    [backendBase, adminToken, userId]
  );

  useEffect(() => {
    const body = document.body;
    const prevBg = body.style.backgroundImage;
    const prevBgSize = body.style.backgroundSize;
    const prevBgPos = body.style.backgroundPosition;
    const prevBgRepeat = body.style.backgroundRepeat;
    const prevClasses = new Set(Array.from(body.classList));
    body.classList.add("game-active");
    body.classList.remove("common-bg", "common-bg1", "rules-bg");
    const bgRaw = resolveThemeBackdropKey(themeData, narrowViewport);
    const bgUrl = getDvSourceImageUrl(bgRaw);
    body.style.backgroundImage = bgUrl ? toCssUrlValue(bgUrl) : prevBg;
    body.style.backgroundSize = "cover";
    body.style.backgroundPosition = "center";
    body.style.backgroundRepeat = "no-repeat";
    return () => {
      body.classList.remove("game-active");
      body.className = "";
      prevClasses.forEach((c) => body.classList.add(c));
      body.style.backgroundImage = prevBg;
      body.style.backgroundSize = prevBgSize;
      body.style.backgroundPosition = prevBgPos;
      body.style.backgroundRepeat = prevBgRepeat;
    };
  }, [narrowViewport, themeData?.background_desk, themeData?.background_mob, themeData?.desktop_view_image, themeData?.mobile_view_image]);

  useEffect(() => {
    if (!themeData || themeStatus === "loading") return;
    if (!adminToken) return;
    const currentTheme = themeData?.themename ?? themeData?.themeName ?? themeData?.themeId ?? null;
    const organizationId = storedUserData?.organizationId || "admin";
    const sessionId = storedUserData?.sessionId || "admin";
    if (!currentTheme) return;
    if (!userId) {
      Swal.fire({
        ...getGameSwalBase(),
        title: "Session",
        text: "Missing user profile. Please open the game from your login link again.",
        icon: "warning",
      });
      return;
    }
    const ac = new AbortController();
    setLoadingQuestions(true);
    (async () => {
      try {
        async function fetchMappedQuestions(signal) {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getAdminQuestions`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
            body: JSON.stringify({ organizationId, sessionId, currentTheme }),
            signal,
          });
          const data = await res.json().catch(() => ({}));
          return mapQuestionsFromApiPayload(data);
        }

        const requiredTotal = parseRequiredTotalQuestions(themeData);
        let mapped = await fetchMappedQuestions(ac.signal);

        /** Themeset expects N (capped); DB/session may lag — themed Swal + refetch until enough or unchanged OK */
        for (;;) {
          if (ac.signal.aborted) return;
          const missing = requiredTotal != null ? Math.max(0, requiredTotal - mapped.length) : 0;
          if (!(requiredTotal != null && missing > 0)) break;

          await Swal.fire({
            ...getGameSwalBase(),
            title: "More questions needed",
            text:
              `Please tell your admin to add questions.` +
              "\n\nTap OK to check again.",
            icon: "warning",
            confirmButtonText: "OK",
          });
          if (ac.signal.aborted) return;
          mapped = await fetchMappedQuestions(ac.signal);
        }

        if (requiredTotal != null && mapped.length > requiredTotal) {
          mapped = mapped.slice(0, requiredTotal);
        }

        setQuestions(mapped);

        let stageNumeric = null;
        if (backendBase && adminToken && userId && mapped.length > 0) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
            body: JSON.stringify({
              userId, email: storedUserData?.email ?? "", emp_id: storedUserData?.employeeId ?? storedUserData?.emp_id ?? "",
              name: storedUserData?.name ?? "", user_type: stageUserTypeLabel(storedUserData),
              themename: currentTheme, themeName: currentTheme, currentTheme, questions: mapped.map((q) => q.sortOrder),
            }),
            signal: ac.signal,
          });
          const startBody = await startRes.json().catch(() => ({}));
          const sid = startBody?.id ?? null;
          stageNumeric = sid != null ? Number(sid) : sid;
          stageIdRef.current = stageNumeric;
          if (!startBody?.success) console.warn("welcomeStageStart:", startBody?.message || startRes.status);

          const pr = startBody?.progress;
          if (pr && Number(pr.gameover) === 1) {
            window.location.replace("/leaderboard");
            return;
          }

          const snapshotCompatible = questionOrderSnapMatches(pr?.questionsSnapshot, mapped);

          let restored = false;
          if (mapped.length > 0 && stageNumeric != null && Number.isFinite(stageNumeric) && pr && snapshotCompatible) {
            const cq = Number(pr.currentQues) || 0;
            /** `currentQues` is 1-based in DB (same as persistProgress); 0 = not started */
            const idxDb =
              cq >= 1 ? Math.min(Math.max(0, cq - 1), mapped.length - 1) : 0;
            answerHistoryRef.current = Array.isArray(pr.answerLog) ? [...pr.answerLog] : [];
            setIdx(idxDb);
            setPoints(Number(pr.total_score) || 0);
            setWrongTotal(Number(pr.wrongcount) || 0);
            setAttempts(0);
            setElapsed(parseMmSsToSeconds(pr.time));
            restored = true;
          }

          if (!restored) {
            answerHistoryRef.current = [];
            setIdx(0);
            setAttempts(0);
            setWrongTotal(0);
            setPoints(0);
            setElapsed(0);
          }
        } else if (!mapped.length) {
          stageIdRef.current = null;
          answerHistoryRef.current = [];
          setIdx(0);
          setAttempts(0);
          setWrongTotal(0);
          setPoints(0);
          setElapsed(0);
        } else {
          stageIdRef.current = null;
          answerHistoryRef.current = [];
          setIdx(0);
          setAttempts(0);
          setWrongTotal(0);
          setPoints(0);
          setElapsed(0);
        }

        setAnswer("");
        reportIdRef.current = readSessionReportId();
      } catch (e) {
        if (e?.name === "AbortError") return;
        Swal.fire({
          ...getGameSwalBase(),
          title: "Error",
          text: e.message || "Failed to load questions",
          icon: "error",
        });
      } finally {
        if (!ac.signal.aborted) setLoadingQuestions(false);
      }
    })();
    return () => ac.abort();
  }, [adminToken, backendBase, getGameSwalBase, storedUserData, themeData, themeStatus, userId]);

  useEffect(() => {
    if (!themeData) return;
    const t = setInterval(() => setElapsed((p) => p + 1), 1000);
    return () => clearInterval(t);
  }, [themeData]);

  useEffect(() => {
    setHintOpen(false);
  }, [idx]);

  const current = questions[idx] || null;
  const questionImgUrl = current?.imageName ? getDvSourceImageUrl(current.imageName) : "";
  const questionHint = String(current?.hint ?? "").trim();
  const landingPageTitle = String(themeData?.landing_page_title ?? "").trim();
  /** At 0:00 lines must fully vanish; otherwise keep a small floor so the bar never looks empty mid-countdown */
  const timerLineFlex =
    useTimeoutQs && questionRemaining >= 0 && questionBudgetSec > 0
      ? questionRemaining === 0
        ? 0
        : Math.max(0.05, questionRemaining / questionBudgetSec)
      : 1;
  const timerUrgent = useTimeoutQs && questionRemaining >= 1 && questionRemaining <= 5;
  const timerCenterText =
    useTimeoutQs && questionRemaining >= 0 ? formatMmSs(questionRemaining) : String(idx + 1);

  const showPointsDelta = (delta) => {
    setPointsFlash({ key: Date.now(), delta });
    window.setTimeout(() => setPointsFlash(null), 950);
  };

  const triggerBuzz = () => {
    setIsBuzzing(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsBuzzing(true));
    });
    setTimeout(() => setIsBuzzing(false), 600);
  };

  const triggerBuzzRef = useRef(triggerBuzz);
  triggerBuzzRef.current = triggerBuzz;

  const goCompleteGame = async ({ score, wrongcount, currentQues }) => {
    const sid = stageIdRef.current;
    await syncExternalReport(score);
    await finalizeStage({ stageId: sid, score, wrongcount, currentQues });
    window.location.assign("/leaderboard");
  };

  const persistProgressRef = useRef(persistProgress);
  persistProgressRef.current = persistProgress;
  const syncExternalReportRef = useRef(syncExternalReport);
  syncExternalReportRef.current = syncExternalReport;
  const getGameSwalBaseRef = useRef(getGameSwalBase);
  getGameSwalBaseRef.current = getGameSwalBase;
  const goCompleteGameRef = useRef(goCompleteGame);
  goCompleteGameRef.current = goCompleteGame;

  const pushAnswerEntry = (entry) => {
    answerHistoryRef.current = [...answerHistoryRef.current, entry];
    return JSON.stringify(answerHistoryRef.current);
  };

  const questionsRef = useRef(questions);
  questionsRef.current = questions;

  const idxRef = useRef(idx);
  idxRef.current = idx;

  const wrongTotalRef = useRef(wrongTotal);
  wrongTotalRef.current = wrongTotal;

  const pointsRef = useRef(points);
  pointsRef.current = points;

  const attemptsRef = useRef(attempts);
  attemptsRef.current = attempts;

  const submitLockedRef = useRef(submitLocked);
  submitLockedRef.current = submitLocked;

  const timeoutRevealBusyRef = useRef(false);

  /** De-dup timeout reveal — same react commit can briefly keep remaining===0 after idx increments */
  const activeTimeoutRevealKeyRef = useRef("");

  /** Reset countdown when advancing to another question */
  useEffect(() => {
    if (!useTimeoutQs || !questions.length || !current || questionBudgetSec < 5) {
      setQuestionRemaining(-1);
      return;
    }
    setQuestionRemaining(questionBudgetSec);
  }, [idx, questionBudgetSec, useTimeoutQs, questions.length, current]);

  /** One-second tick — pauses while submitLocked (e.g. processing answer) */
  useEffect(() => {
    if (!useTimeoutQs || loadingQuestions || !current || submitLocked || questionBudgetSec < 5) return undefined;
    if (questionRemaining < 0) return undefined;
    const id = window.setInterval(() => {
      setQuestionRemaining((p) => (p <= 0 ? 0 : p - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [idx, questionBudgetSec, useTimeoutQs, loadingQuestions, submitLocked, current]);

  /** Time over → same flow as Answer reveal dialog + advance */
  useEffect(() => {
    if (!useTimeoutQs || questionRemaining !== 0 || !current || loadingQuestions || submitLockedRef.current) return undefined;

    const turnKey = `${idx}:${current.sortOrder ?? ""}`;
    if (activeTimeoutRevealKeyRef.current === turnKey) return undefined;

    activeTimeoutRevealKeyRef.current = turnKey;
    timeoutRevealBusyRef.current = false;

    let cancelled = false;
    const run = async () => {
      if (cancelled || timeoutRevealBusyRef.current) return;
      timeoutRevealBusyRef.current = true;
      flushSync(() => setSubmitLocked(true));

      triggerBuzzRef.current();

      const i = idxRef.current;
      const cur = questionsRef.current[i];
      if (!cur) {
        timeoutRevealBusyRef.current = false;
        activeTimeoutRevealKeyRef.current = "";
        flushSync(() => setSubmitLocked(false));
        focusAnswerInput();
        return;
      }

      const cqActive = i + 1;
      const sortOrder = cur.sortOrder ?? cqActive;
      const sid = stageIdRef.current;

      const nextWt = wrongTotalRef.current + 1;
      const scoreHold = pointsRef.current;

      const answerJsonWrong = pushAnswerEntry({
        sort_order: sortOrder,
        submitted: "(time up)",
        correct: false,
        at: new Date().toISOString(),
      });

      setWrongTotal(nextWt);

      const persistedWrong = await persistProgressRef.current({
        stageId: sid,
        score: scoreHold,
        wrongcount: nextWt,
        currentQues: cqActive,
        answerJson: answerJsonWrong,
      });
      if (persistedWrong) await syncExternalReportRef.current(scoreHold);

      if (cancelled) return;

      await Swal.fire({
        ...getGameSwalBaseRef.current(),
        title: "Answer reveal",
        text: `Correct answer: ${cur.answer || "—"}`,
        confirmButtonText: "OK",
      });

      if (cancelled) return;

      const nextIdx = i + 1;
      const qLen = questionsRef.current.length;
      const cqNext = nextIdx < qLen ? nextIdx + 1 : qLen;
      const persistedReveal = await persistProgressRef.current({
        stageId: sid,
        score: scoreHold,
        wrongcount: nextWt,
        currentQues: cqNext,
        answerJson: JSON.stringify(answerHistoryRef.current),
      });
      if (persistedReveal) await syncExternalReportRef.current(scoreHold);

      if (nextIdx >= qLen) {
        activeTimeoutRevealKeyRef.current = "";
        timeoutRevealBusyRef.current = false;
        await goCompleteGameRef.current({ score: scoreHold, wrongcount: nextWt, currentQues: qLen });
        return;
      }
      /** Bump remaining BEFORE idx so we never have (remaining===0 && idx already advanced) — that re-fired reveal */
      flushSync(() => {
        setQuestionRemaining(questionBudgetSec);
        setIdx(nextIdx);
        setAttempts(0);
        setAnswer("");
      });
      activeTimeoutRevealKeyRef.current = "";
      setSubmitLocked(false);
      timeoutRevealBusyRef.current = false;
      focusAnswerInput();
    };

    void run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on countdown expiry
  }, [questionRemaining, questionBudgetSec, useTimeoutQs, idx, current?.sortOrder, loadingQuestions]);

  const onSubmit = async () => {
    if (!current) return;
    const typed = normalizeAnswer(answer);
    if (!typed) return;
    if (submitLocked) return;
    setSubmitLocked(true);

    try {
      /** Demo: first submit (right or wrong) — thank-you modal then plans page */
      if (isDemoSessionUser(readSessionUserData())) {
        const sid = stageIdRef.current;
        const correct = normalizeAnswer(current.answer);
        const cqActive = idx + 1;
        const sortOrder = current.sortOrder ?? cqActive;
        if (typed === correct) {
          const newPoints = points + rightPoints;
          const answerJson = pushAnswerEntry({
            sort_order: sortOrder,
            submitted: answer.trim(),
            correct: true,
            at: new Date().toISOString(),
          });
          setPoints(newPoints);
          showPointsDelta(+rightPoints);
          fireConfetti();
          const nextIdx = idx + 1;
          const cqPersist = nextIdx >= questions.length ? questions.length : nextIdx + 1;
          await persistProgress({
            stageId: sid,
            score: newPoints,
            wrongcount: wrongTotal,
            currentQues: cqPersist,
            answerJson,
          });
        } else {
          const nextWrongTotal = wrongTotal + 1;
          setWrongTotal(nextWrongTotal);
          setBlockedStrikeFlash(true);
          window.setTimeout(() => setBlockedStrikeFlash(false), 950);
          triggerBuzz();
          const answerJsonWrong = pushAnswerEntry({
            sort_order: sortOrder,
            submitted: answer.trim(),
            correct: false,
            at: new Date().toISOString(),
          });
          await persistProgress({
            stageId: sid,
            score: points,
            wrongcount: nextWrongTotal,
            currentQues: cqActive,
            answerJson: answerJsonWrong,
          });
          setAnswer("");
        }
        await Swal.fire({
          ...getGameSwalBase(),
          text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
          confirmButtonText: "OK",
        });
        const plansBase = String(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
        window.location.assign(`${plansBase}/plans`);
        return;
      }

      const sid = stageIdRef.current;
      const correct = normalizeAnswer(current.answer);
      const cqActive = idx + 1;
      const sortOrder = current.sortOrder ?? cqActive;

      if (typed === correct) {
        const newPoints = points + rightPoints;
        const answerJson = pushAnswerEntry({ sort_order: sortOrder, submitted: answer.trim(), correct: true, at: new Date().toISOString() });
        setPoints(newPoints);
        showPointsDelta(+rightPoints);
        fireConfetti();

        const nextIdx = idx + 1;
        const cqPersist = nextIdx >= questions.length ? questions.length : nextIdx + 1;
        const persisted = await persistProgress({ stageId: sid, score: newPoints, wrongcount: wrongTotal, currentQues: cqPersist, answerJson });
        if (persisted) await syncExternalReport(newPoints);

        setTimeout(() => {
          if (nextIdx >= questions.length) {
            void goCompleteGame({ score: newPoints, wrongcount: wrongTotal, currentQues: questions.length });
            return;
          }
          flushSync(() => {
            if (useTimeoutQs) setQuestionRemaining(questionBudgetSec);
            setIdx(nextIdx);
            setAttempts(0);
            setAnswer("");
          });
          activeTimeoutRevealKeyRef.current = "";
          setSubmitLocked(false);
          focusAnswerInput();
        }, 220);
        return;
      }

      const nextWrongTotal = wrongTotal + 1;
      setWrongTotal(nextWrongTotal);
      setBlockedStrikeFlash(true);
      window.setTimeout(() => setBlockedStrikeFlash(false), 950);
      triggerBuzz();

      const answerJsonWrong = pushAnswerEntry({ sort_order: sortOrder, submitted: answer.trim(), correct: false, at: new Date().toISOString() });
      const persistedWrong = await persistProgress({ stageId: sid, score: points, wrongcount: nextWrongTotal, currentQues: cqActive, answerJson: answerJsonWrong });
      if (persistedWrong) await syncExternalReport(points);

      const nextAttempts = attempts + 1;
      setAttempts(nextAttempts);
      setAnswer("");

      if (nextAttempts >= MAX_ATTEMPTS) {
        await Swal.fire({
          ...getGameSwalBase(),
          title: "Answer reveal",
          text: `Correct answer: ${current.answer || "—"}`,
          confirmButtonText: "OK",
        });
        const nextIdx = idx + 1;
        const cqNext = nextIdx < questions.length ? nextIdx + 1 : questions.length;
        const persistedReveal = await persistProgress({ stageId: sid, score: points, wrongcount: nextWrongTotal, currentQues: cqNext, answerJson: JSON.stringify(answerHistoryRef.current) });
        if (persistedReveal) await syncExternalReport(points);
        if (nextIdx >= questions.length) {
          await goCompleteGame({ score: points, wrongcount: nextWrongTotal, currentQues: questions.length });
          return;
        }
        flushSync(() => {
          if (useTimeoutQs) setQuestionRemaining(questionBudgetSec);
          setIdx(nextIdx);
          setAttempts(0);
          setAnswer("");
        });
        activeTimeoutRevealKeyRef.current = "";
      }
      setSubmitLocked(false);
    } catch (e) {
      console.warn("onSubmit", e);
      setSubmitLocked(false);
    }
  };

  if (themeStatus === "loading" || !themeData) {
    return (
      <div className="fullhouse-page">
        <div className="fullhouse-loader" />
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="game-container">
      {/* Confetti canvas — fullscreen above app chrome */}
      <canvas ref={canvasRef} className="confetti-canvas confetti-canvas--fullscreen" />

      {/* ── TOP BAR: Rules LEFT | Logo center | Score RIGHT (timer hidden; time still tracked for API) ── */}
      <div className="top-bar" style={{ color: textColor }}>
        <div className="top-bar__left top-bar__left--pills">
          <button
            type="button"
            className="icon-pill rules-pill"
            onClick={() => setRulesOpen(true)}
            style={{ background: iconBg, color: textColor }}
            aria-label="How to play"
          >
            ?
          </button>
          <button
            type="button"
            className="icon-pill rules-pill game-hint-pill"
            onClick={() => questionHint && setHintOpen(true)}
            style={{ background: iconBg, color: textColor }}
            aria-label={questionHint ? "View hint for this question" : "No hint for this question"}
            disabled={!questionHint}
          >
            <i className="fa-solid fa-lightbulb" aria-hidden />
          </button>
        </div>
        <div className="top-bar__center">
          <div className="top-bar__logo">
            {String(themeData?.logo_text ?? "").trim() || ""}
          </div>
        </div>
        <div className="top-bar__right">
          <div
            className="icon-pill"
            style={{ background: iconBg, color: textColor, position: "relative" }}
          >
            <SvgIconStar size={17} />
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{points}</span>
            {pointsFlash && (
              <span key={pointsFlash.key} className="pts-flash" style={{ color: pointsFlash.delta > 0 ? "#4ade80" : "#f87171" }}>
                {pointsFlash.delta > 0 ? `+${pointsFlash.delta}` : pointsFlash.delta}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Wrong-answer shake (buzz) only on this panel, below the header */}
      <div
        className={`game-main-panel${isBuzzing ? " game-fx-wrong" : ""}`}
        style={{
          position: "relative",
          ["--game-panel-box-local"]: boxColorSolid,
          ["--game-text-color"]: textColor,
          ["--game-icon-bg"]: iconBg,
        }}
      >
        {blockedStrikeFlash ? (
          <div className="game-symbol-flash" aria-hidden>
            <span className="game-symbol-flash__emoji">🚫</span>
          </div>
        ) : null}

        {landingPageTitle ? (
          <div className="game-landing-title" style={{ color: textColor }}>
            {landingPageTitle}
          </div>
        ) : null}

        {/* ── TIMER / QUESTION BAR (lines shorten as countdown drains when use_timeout) ── */}
        <div className={`q-count-bar${timerUrgent ? " q-count-bar--urgent" : ""}${useTimeoutQs ? " q-count-bar--timed" : ""}`}>
          <div className="q-count-bar__track q-count-bar__track--left">
            <span
              className="q-count-bar__line-strip"
              style={{
                ...(useTimeoutQs ? { transform: `scaleX(${timerLineFlex})`, transformOrigin: "right center" } : {}),
              }}
              aria-hidden
            />
          </div>
          <div className={`q-count-bar__badge${timerUrgent ? " q-count-bar__badge--blink" : ""}`}>{timerCenterText}</div>
          <div className="q-count-bar__track q-count-bar__track--right">
            <span
              className="q-count-bar__line-strip"
              style={{
                ...(useTimeoutQs ? { transform: `scaleX(${timerLineFlex})`, transformOrigin: "left center" } : {}),
              }}
              aria-hidden
            />
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div className="game-content">
        {loadingQuestions ? (
          <div style={{ color: textColor, fontSize: 18, fontWeight: 700 }}>Loading questions…</div>
        ) : !current ? (
          <div style={{ color: textColor, fontSize: 18, fontWeight: 700 }}>No questions found.</div>
        ) : (
          <>
            <div key={idx} className={`game-q-transition${narrowViewport ? " game-q-transition--mobile" : ""}`}>
              {(questionEmojiLayout.glyphs.length > 0 || questionImgUrl) && (
                <div
                  className={`question-img-wrap${questionEmojiLayout.glyphs.length > 0 ? " question-img-wrap--emoji question-img-wrap--emoji-cells" : ""}`}
                  style={
                    questionEmojiLayout.glyphs.length > 0
                      ? undefined
                      : {
                          background: boxColorSolid,
                          padding: "clamp(10px, 3vw, 18px)",
                          borderRadius: 16,
                          boxSizing: "border-box",
                          boxShadow: "0 14px 40px rgba(0,0,0,0.18)",
                        }
                  }
                >
                  {questionEmojiLayout.glyphs.length > 0 ? (
                    <span
                      className={`question-emoji-row${questionEmojiLayout.glyphs.length >= 6 ? " question-emoji-row--compact" : ""}`}
                      role="img"
                      aria-label={`Question ${idx + 1}`}
                    >
                      {questionEmojiLayout.glyphs.map((g, i) => (
                        <React.Fragment key={`${i}-${g}`}>
                          {i > 0 ? (
                            <span className="question-emoji-between" aria-hidden>
                              +
                            </span>
                          ) : null}
                          <span
                            className="question-emoji-cell"
                            style={{
                              background: boxColorSolid,
                              width: questionEmojiLayout.cellPx,
                              height: questionEmojiLayout.cellPx,
                              minWidth: questionEmojiLayout.cellPx,
                              minHeight: questionEmojiLayout.cellPx,
                            }}
                          >
                            <Emoji
                              unified={graphemeToUnified(g)}
                              emojiStyle={EmojiStyle.APPLE}
                              size={questionEmojiLayout.emojiPx}
                            />
                          </span>
                        </React.Fragment>
                      ))}
                    </span>
                  ) : (
                    <img
                      src={questionImgUrl}
                      alt={`Question ${idx + 1}`}
                      className="question-img"
                      draggable={false}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="answer-equals-row" aria-hidden="true">
              <span className="answer-equals-row__eq">=</span>
            </div>
            {/* Answer input — bg icon_bg_color, typed text textcolor (vars on .game-main-panel) */}
            <div className="input-row">
              <input
                ref={answerInputRef}
                value={answer}
                maxLength={MAX_ANSWER_LEN}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Type your answer"
                className="answer-input"
                onKeyDown={(e) => { if (e.key === "Enter") onSubmit(); }}
              />
            </div>

          

            <div className="lifelines-strip">
              <div className="lifelines">
                {Array.from({ length: MAX_ATTEMPTS }).map((_, i) => (
                  <span key={i} className="lifeline-icon" style={{ opacity: i < attempts ? 1 : 0.25 }}>
                    🚫
                  </span>
                ))}
              </div>
            </div>

            {/* Submit */}
            <div className="submit-row">
              <button
                type="button"
                onClick={onSubmit}
                className="submit-btn"
                style={{ background: buttonColor, color: buttonTextColor }}
                disabled={!current || submitLocked || !String(answer ?? "").trim()}
              >
                SUBMIT
              </button>
            </div>
          </>
        )}
        </div>
      </div>

      {/* ── RULES MODAL ── */}
      {rulesOpen && (
        <div className="rules-modal-overlay" role="presentation">
          <div
            ref={rulesDialogRef}
            className={`rules-modal${hasPopupBackdropImage ? " rules-modal--photo" : " rules-modal--no-photo"}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="rules-modal-title"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            style={{
              color: popupFgColor,
              border: `1px solid rgba(255,255,255,0.35)`,
            }}
          >
            <h2 id="rules-modal-title" style={{ marginTop: 0, marginBottom: 12, fontSize: "clamp(1.05rem, 4.2vw, 1.45rem)" }}>
              HOW TO PLAY
            </h2>
            <div className="rules-content">
              {(Array.isArray(themeData?.rules) ? themeData.rules : []).map((r, i) => (
                <div key={i} className="rules-content__item">
                  ☐ {String(r)}
                </div>
              ))}
            </div>
            <button
              type="button"
              className="close-btn"
              style={{ background: buttonColor, color: buttonTextColor }}
              onClick={() => {
                setRulesOpen(false);
                window.setTimeout(() => focusAnswerInput(), 0);
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      {hintOpen && questionHint && (
        <div className="rules-modal-overlay" role="presentation">
          <div
            ref={hintDialogRef}
            className={`rules-modal${hasPopupBackdropImage ? " rules-modal--photo" : " rules-modal--no-photo"}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="hint-modal-title"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            style={{
              color: popupFgColor,
              border: `1px solid rgba(255,255,255,0.35)`,
            }}
          >
            <h2 id="hint-modal-title" style={{ marginTop: 0, marginBottom: 12, fontSize: "clamp(1.05rem, 4.2vw, 1.45rem)", textAlign: "center" }}>
              HINT
            </h2>
            <div className="rules-content">
              <div className="rules-content__item">{questionHint}</div>
            </div>
            <button
              type="button"
              className="close-btn"
              style={{ background: buttonColor, color: buttonTextColor }}
              onClick={() => {
                setHintOpen(false);
                window.setTimeout(() => focusAnswerInput(), 0);
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
